<template>
  <v-dialog v-model="dialog" max-width="500px">
    <v-card>
      <v-card-title class="headline">
        <v-icon :color="actionColor" class="mr-2">{{ actionIcon }}</v-icon>
        {{ actionTitle }}
      </v-card-title>
      
      <v-card-text>
        <p class="mb-3">
          {{ actionMessage }}
        </p>
        
        <v-text-field
          v-if="actionType !== 'reactivate'"
          v-model="reason"
          label="Motivo (opcional)"
          placeholder="Digite o motivo da ação..."
          outlined
          dense
          rows="3"
          auto-grow
        ></v-text-field>
        
        <div v-if="project" class="mt-3">
          <strong>Projeto:</strong> {{ project.project_name }}<br>
          <strong>Cliente:</strong> {{ project.lead_name }}
          <div v-if="actionType === 'reactivate' && project.steps" class="mt-2">
            <strong>Passo atual:</strong> {{ getCurrentStepName(project) }}<br>
            <strong>Dias inativo:</strong> {{ getDaysInactive(project) }} dias
          </div>
        </div>
      </v-card-text>
      
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="cancel">
          Cancelar
        </v-btn>
        <v-btn 
          :color="actionColor" 
          @click="confirm"
          :loading="loading"
        >
          {{ actionButtonText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'ProjectActionDialog',
  
  props: {
    value: {
      type: Boolean,
      default: false
    },
    project: {
      type: Object,
      default: null
    },
    actionType: {
      type: String,
      default: 'decline', // 'decline', 'reject' ou 'reactivate'
      validator: value => ['decline', 'reject', 'reactivate'].includes(value)
    }
  },
  
  data: () => ({
    reason: '',
    loading: false
  }),
  
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    
    actionTitle() {
      if (this.actionType === 'decline') return 'Declinar Projeto';
      if (this.actionType === 'reject') return 'Reprovar Projeto';
      return 'Reativar Projeto';
    },
    
    actionMessage() {
      if (this.actionType === 'decline') {
        return 'Tem certeza que deseja declinar este projeto? Esta ação moverá o projeto para a coluna "Declinados".';
      }
      if (this.actionType === 'reject') {
        return 'Tem certeza que deseja reprovar este projeto? Esta ação moverá o projeto para a coluna "Reprovados".';
      }
      const stepName = this.getCurrentStepName(this.project);
      return `Tem certeza que deseja reativar este projeto? O projeto será reativado para o passo "${stepName}".`;
    },
    
    actionButtonText() {
      if (this.actionType === 'decline') return 'Declinar';
      if (this.actionType === 'reject') return 'Reprovar';
      return 'Reativar';
    },
    
    actionColor() {
      if (this.actionType === 'decline') return 'error';
      if (this.actionType === 'reject') return 'warning';
      return 'success';
    },
    
    actionIcon() {
      if (this.actionType === 'decline') return 'mdi-close-circle';
      if (this.actionType === 'reject') return 'mdi-alert-circle';
      return 'mdi-play-circle';
    }
  },
  
  watch: {
    dialog(newVal) {
      if (!newVal) {
        this.resetForm();
      }
    }
  },
  
  methods: {
    async confirm() {
      this.loading = true;
      
      try {
        const actionData = {
          projectId: this.project.id_doc,
          actionType: this.actionType,
          reason: this.reason.trim(),
          project: this.project
        };
        
        this.$emit('confirm', actionData);
        this.dialog = false;
      } catch (error) {
        console.error('Erro ao executar ação:', error);
        this.$emit('error', error);
      } finally {
        this.loading = false;
      }
    },
    
    cancel() {
      this.dialog = false;
    },
    
    resetForm() {
      this.reason = '';
      this.loading = false;
    },
    
    getCurrentStepName(project) {
      if (!project || !project.steps) return 'N/A';
      const currentStep = project.steps.find(step => step.isCurrentStep === true);
      if (!currentStep) return 'N/A';
      
      const stepNames = {
        'prospecting': 'Prospecção',
        'diagnostic': 'Waterscan',
        'approval': 'Aprovação',
        'closing': 'Fechamento',
        'contracts': 'Contratos',
        'materials': 'Entrega',
        'installation': 'Waterfix',
        'finish': 'Finalizado'
      };
      
      return stepNames[currentStep.name] || currentStep.name;
    },
    
    getDaysInactive(project) {
      if (!project) return 0;
      const lastActivity = project.currentHourStatusFranchisee || project.currentHourStatusADM || project.created_at_formatted;
      const lastActivityDate = new Date(lastActivity);
      const now = new Date();
      const daysInactive = Math.floor((now - lastActivityDate) / (1000 * 60 * 60 * 24));
      return daysInactive;
    }
  }
}
</script>

<style scoped>
.v-card__title {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 16px;
}

.v-card__actions {
  border-top: 1px solid #e0e0e0;
  padding-top: 16px;
}
</style>